import * as amplitude from '@amplitude/analytics-browser';
import * as Sentry from '@sentry/react';

import { createResponseValidationReporter } from './responseValidation';

export type Environment = 'development' | 'beta' | 'staging' | 'production';

type LoggerFactoryProps = {
  environment: Environment;
  /**
   * Heartbeat sampling rate for repeat (already-reported) response-validation
   * failures. First-seen shapes and entities always capture regardless.
   */
  validationSampleRate?: number;
};

type LogProps = {
  breadcrumb?: Sentry.Breadcrumb;
  data?: any;
  isProduction: boolean;
  level: Sentry.SeverityLevel;
  message: string;
};

type LoggerProps = Pick<LogProps, 'message' | 'breadcrumb' | 'data'>;

type ErrorLoggerProps = Omit<LoggerProps, 'data'> & { error: any };

const isError = (error: any): error is Error => error instanceof Error;

const SENTRY_SEVERITY_MAP: Record<
  Sentry.SeverityLevel,
  (...data: any) => void
> = {
  debug: console.debug,
  error: console.error,
  fatal: console.error,
  info: console.info,
  log: console.log,
  warning: console.warn,
};

const addBreadcrumb = ({
  breadcrumb,
  data,
  level,
  message,
}: {
  breadcrumb: Sentry.Breadcrumb;
  data?: any;
  level: Sentry.SeverityLevel;
  message?: string;
}) => {
  if (!Sentry.isInitialized()) {
    return;
  }

  Sentry.addBreadcrumb({
    category: breadcrumb.category ?? 'info',
    data,
    level,
    message: message ?? breadcrumb.message ?? '',
    type: breadcrumb.type ?? 'info',
  });
};

const captureException = (message: string, data: any): void => {
  if (!Sentry.isInitialized()) {
    return;
  }

  if (isError(data)) {
    Sentry.captureException(data, { extra: { message } });
  } else {
    Sentry.captureException(new Error(message), { extra: data });
  }
};

const log = ({ message, isProduction, level, data, breadcrumb }: LogProps) => {
  if (!isProduction) {
    const consoleLevel = SENTRY_SEVERITY_MAP[level];
    consoleLevel(message, data ?? '');
  }

  if (breadcrumb) {
    addBreadcrumb({
      breadcrumb,
      data,
      level,
      message,
    });
  }

  if (level === 'error') {
    captureException(message, data);
  }
};

export const loggerFactory = ({
  environment,
  validationSampleRate = 0.02,
}: LoggerFactoryProps) => {
  const isProduction = environment === 'production';
  const responseValidation = createResponseValidationReporter({
    isProduction,
    sampleRate: validationSampleRate,
  });

  return {
    debug(props: LoggerProps) {
      log({
        ...props,
        isProduction,
        level: 'debug',
      });
    },

    error({ error, ...rest }: ErrorLoggerProps) {
      log({
        ...rest,
        data: error,
        isProduction,
        level: 'error',
      });
    },

    info(props: LoggerProps) {
      log({
        ...props,
        isProduction,
        level: 'info',
      });
    },

    productAnalyticsEvent(
      event: string,
      feature: string,
      type?: string,
      eventProperties?: Record<string, any>,
    ) {
      if (typeof amplitude?.track !== 'function') {
        return;
      }

      if (type) {
        amplitude.track(`${feature} - (${type}) ${event}`, eventProperties);
        return;
      }

      amplitude.track(`${feature} - ${event}`, eventProperties);
    },

    responseValidation,

    warn(props: LoggerProps) {
      log({
        ...props,
        isProduction,
        level: 'warning',
      });
    },
  };
};
